import { SocketClient } from "cbcore-ts"
import { IF, NO, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CompanyRelationshipPolicyLabels } from "../CompanyView/CompanyRelationships/CompanyRelationshipPolicyLabels"
import { RHCore } from "../Core/RHCore"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHLoginViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { LoginViewController } from "../Login/LoginViewController"
import { RHCompanyRelationshipRequest } from "../SocketClientFunctions"
import { populatedValueOfReference } from "../Core/RHReference"


// Reached pre-navigation via the e-mail link generated by
// CompanyRelationshipApprovalService/EmailService ("companyRelationshipApproval" template):
// #company_relationship_approval[token:...]. Approving/rejecting still requires the visitor
// to be logged in as the approving company's administrator (the server re-verifies this),
// so this screen shows a "please log in" state first when needed, then the exact requested
// policy in plain language, then lets the visitor approve or reject.
export class CompanyRelationshipApprovalViewController extends UIViewController {

    readonly titleLabel = new UITextView(this.view.elementID + "TitleLabel", UITextView.type.header4)
        .configuredWithObject({
            text: "Ettevõtete seose kinnitamine",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXXLarge,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)

    readonly statusBannerView = new RHAlertBannerView()
        .configuredWithObject({ hidden: YES })
        .addedAsSubviewToView(this.view)

    readonly detailsLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColor.textSecondary,
            hidden: YES
        })
        .addedAsSubviewToView(this.view)

    readonly policyLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            hidden: YES
        })
        .addedAsSubviewToView(this.view)

    readonly logInButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Logi sisse" },
            variant: "success",
            hidden: YES,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    UIRoute.currentRoute
                        .routeWithViewControllerComponent(LoginViewController, {})
                        .apply()
                }
            }
        })
        .addedAsSubviewToView(this.view)

    readonly approveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kinnita seos" },
            variant: "success",
            hidden: YES,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async () => {
                    await this._approve()
                }
            }
        })
        .addedAsSubviewToView(this.view)

    readonly rejectButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Lükka tagasi" },
            variant: RHButtons.destructiveUtilityVariant,
            hidden: YES,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async () => {
                    await this._reject()
                }
            }
        })
        .addedAsSubviewToView(this.view)

    override _focusManager = new RHFocusManager(() => [
        this.logInButton,
        this.approveButton,
        this.rejectButton
    ])

    _token = ""
    _pendingRequest?: RHCompanyRelationshipRequest
    _approvalSide?: "parent" | "child"


    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.backgroundColor })
    }


    static override readonly routeComponentName = "company_relationship_approval"

    static override readonly ParameterIdentifierName = {
        token: "token"
    }


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager!)
    }


    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager!)
    }


    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)

        const component = route.componentWithName(CompanyRelationshipApprovalViewController.routeComponentName)
        this._token = component?.parameters[
            CompanyRelationshipApprovalViewController.ParameterIdentifierName.token
        ] ?? ""

        if (!this._token) {
            this._showError("Vigane või puuduv link.")
            return
        }

        await this._loadPreview()
    }


    async _loadPreview() {
        await RHCore.sharedInstance.sessionInitialised

        if (!RHCore.sharedInstance.isUserLoggedIn) {
            this.detailsLabel.hidden = YES
            this.policyLabel.hidden = YES
            this.approveButton.hidden = YES
            this.rejectButton.hidden = YES
            this.statusBannerView.configureWithObject({
                text: "Seose kinnitamiseks pead olema sisse logitud ettevõtte peakasutajana.",
                alertType: RHAlertBannerView.type.Warning,
                hidden: NO
            })
            this.logInButton.hidden = NO
            this.view.setNeedsLayoutUpToRootView()
            return
        }

        this.logInButton.hidden = YES

        try {
            const response = await SocketClient.PreviewCompanyRelationshipApprovalToken({
                token: this._token
            })
            const result = response.result

            if (!result?.success) {
                this._showError(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Link on aegunud, juba kasutatud või vigane."
                    ) ?? "Link on aegunud, juba kasutatud või vigane."
                )
                return
            }

            if (result.isExpired) {
                this._showError("Selle seose kinnitamise link on aegunud. Palun taotle seos uuesti.")
                return
            }
            if (result.request.status === "rejected") {
                this._showError("See seose taotlus on juba tagasi lükatud.")
                return
            }
            if (result.request.status === "confirmed") {
                this._showError("See seos on juba kinnitatud.")
                return
            }

            this._pendingRequest = result.request
            this._approvalSide = result.approvalSide
            this._showPreview(result.request, result.approvalSide)
        }
        catch (error) {
            console.error("Preview company relationship approval error:", error)
            this._showError("Link on aegunud, juba kasutatud või vigane.")
        }
    }


    _showPreview(request: RHCompanyRelationshipRequest, approvalSide: "parent" | "child") {
        const parentCompany = request.parentCompany
        const childCompany = request.childCompany
        const ourCompany = IF(approvalSide === "parent")(() => parentCompany).ELSE(() => childCompany)
        const otherCompany = IF(approvalSide === "parent")(() => childCompany).ELSE(() => parentCompany)
        const directionLabel = IF(approvalSide === "parent")(() =>
            `"${ourCompany?.name}" on emaettevõte, "${otherCompany?.name}" on tütarettevõte.`
        ).ELSE(() =>
            `"${otherCompany?.name}" on emaettevõte, "${ourCompany?.name}" on tütarettevõte.`
        )

        this.statusBannerView.hidden = YES
        this.detailsLabel.configureWithObject({
            hidden: NO,
            text:
                `Seose liik: ${CompanyRelationshipPolicyLabels.labelForKind(request.relationshipKind)}<br>` +
                `${directionLabel}<br>` +
                `Taotleja: ${populatedValueOfReference(request.requestedByCompany)?.name ?? ""}<br>` +
                `Aegub: ${new Date(request.expiresAt).toLocaleString("et-EE")}`
        })
        this.policyLabel.configureWithObject({
            hidden: NO,
            text:
                "Milliseid õigusi see seos annaks:<br>— " +
                CompanyRelationshipPolicyLabels.activeLabelsForPolicy(request.requestedPolicy).join("<br>— ")
        })
        this.approveButton.hidden = NO
        this.rejectButton.hidden = NO
        this.view.setNeedsLayoutUpToRootView()
    }


    async _approve() {
        this.approveButton.enabled = NO
        this.rejectButton.enabled = NO
        try {
            const response = await SocketClient.ConfirmCompanyRelationshipApproval({
                token: this._token
            })
            if (response.result?.success) {
                this._showSuccess("Seos on kinnitatud.")
            }
            else {
                this._showError(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Kinnitamine ebaõnnestus."
                    ) ?? "Kinnitamine ebaõnnestus."
                )
            }
        }
        catch (error) {
            console.error("Confirm company relationship approval error:", error)
            this._showError("Kinnitamisel esines viga. Palun proovi uuesti.")
        }
    }


    async _reject() {
        // Mirrors the in-app CompanyRelationshipsView reject flow's confirmation step - rejecting
        // is just as hard to undo here (the other side would need to submit a brand new request).
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kas oled kindel, et soovid selle seose taotluse tagasi lükata?"
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            this.approveButton.enabled = NO
            this.rejectButton.enabled = NO
            try {
                const response = await SocketClient.RejectCompanyRelationshipApproval({
                    token: this._token
                })
                if (response.result?.success) {
                    this._showSuccess("Seos on tagasi lükatud.")
                }
                else {
                    this._showError(
                        RHCore.sharedInstance.errorMessageFromResponse(
                            response,
                            "Tagasilükkamine ebaõnnestus."
                        ) ?? "Tagasilükkamine ebaõnnestus."
                    )
                }
            }
            catch (error) {
                console.error("Reject company relationship approval error:", error)
                this._showError("Tagasilükkamisel esines viga. Palun proovi uuesti.")
            }
        }
    }


    _showSuccess(message: string) {
        this.detailsLabel.hidden = YES
        this.policyLabel.hidden = YES
        this.approveButton.hidden = YES
        this.rejectButton.hidden = YES
        this.logInButton.hidden = YES
        this.statusBannerView.configureWithObject({
            text: message,
            alertType: RHAlertBannerView.type.Success,
            hidden: NO
        })
        this.view.setNeedsLayoutUpToRootView()
    }


    _showError(message: string) {
        this.detailsLabel.hidden = YES
        this.policyLabel.hidden = YES
        this.approveButton.hidden = YES
        this.rejectButton.hidden = YES
        this.logInButton.hidden = YES
        this.statusBannerView.configureWithObject({
            text: message,
            alertType: RHAlertBannerView.type.Error,
            hidden: NO
        })
        this.view.setNeedsLayoutUpToRootView()
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        const contentBounds = bounds.rectangleWithMaxWidth(RHLoginViewTokens.authContentMaxWidth, 0.5)

        contentBounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)

            .IF(!this.statusBannerView.hidden)
            .rectangleForNextRow(padding, this.statusBannerView)
            .assignedAsFrameOfView(this.statusBannerView)
            .ENDIF()

            .IF(!this.detailsLabel.hidden)
            .rectangleForNextRow(padding, this.detailsLabel)
            .assignedAsFrameOfView(this.detailsLabel)
            .ENDIF()

            .IF(!this.policyLabel.hidden)
            .rectangleForNextRow(padding, this.policyLabel)
            .assignedAsFrameOfView(this.policyLabel)
            .ENDIF()

            .IF(!this.logInButton.hidden)
            .rectangleForNextRow(padding, RHButtons.compactPrimaryHeight)
            .rectangleWithWidth(220, 0)
            .assignedAsFrameOfView(this.logInButton)
            .ENDIF()

            .IF(!this.approveButton.hidden)
            .rectangleForNextRow(padding, RHButtons.compactPrimaryHeight)
            .distributeViewsEquallyAlongWidth(
                [this.approveButton, this.rejectButton],
                RHSpacing.medium
            )
            .ENDIF()
    }

}
